import React from 'react';
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import WelcomeNamePage from "@/pages/WelcomeNamePage";
import FeedPage from "@/pages/FeedPage";
import ProduceDetailPage from "@/pages/ProduceDetailPage";
import RequirementDetailPage from "@/pages/RequirementDetailPage";
import MyRequirementsPage from "@/pages/MyRequirementsPage";
import FarmerDashboard from "@/pages/FarmerDashboard";
import CreatePostPage from "@/pages/CreatePostPage";
import KYCApplicationPage from "@/pages/KYCApplicationPage";
import AdminPage from "@/pages/AdminPage";
import OrgPage from "@/pages/OrgPage";
import OrgRegisterPage from "@/pages/OrgRegisterPage";
import ProfilePage from "@/pages/ProfilePage";
import SavedPage from "@/pages/SavedPage";
import FarmerProfilePage from "@/pages/FarmerProfilePage";
import FarmerReviewsPage from "@/pages/FarmerReviewsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import LikedFarmersPage from "@/pages/LikedFarmersPage";

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0B4F3F] border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Mandatory name capture: any logged-in user without a name must complete /welcome first.
  if (user.role !== 'admin' && !user.name) return <Navigate to="/welcome" replace />;
  // Admins can access farmer-only routes (so they can post/manage produce as a fallback)
  if (requiredRole && user.role !== requiredRole && user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading, isAdminOnlySession } = useAuth();
  const isAdminOnlyRedirect = () => isAdminOnlySession && isAdminOnlySession();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0B4F3F] border-t-transparent" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={isAdminOnlyRedirect() ? "/admin" : (!user.name ? "/welcome" : "/")} replace /> : <LoginPage />} />
      <Route path="/welcome" element={
        !user ? <Navigate to="/login" replace /> :
        isAdminOnlyRedirect() ? <Navigate to="/admin" replace /> :
        user.name ? <Navigate to="/" replace /> :
        <WelcomeNamePage />
      } />
      {/* Every signed-in user (including admins) can browse the seller app freely.
          Admins jump back to /admin via the "Admin panel" entry on Profile. */}
      <Route path="/" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
      <Route path="/produce/:id" element={<ProtectedRoute><ProduceDetailPage /></ProtectedRoute>} />
      <Route path="/requirement/:id" element={<ProtectedRoute><RequirementDetailPage /></ProtectedRoute>} />
      <Route path="/farmer/:farmerId" element={<ProtectedRoute><FarmerProfilePage /></ProtectedRoute>} />
      <Route path="/farmer/:farmerId/reviews" element={<ProtectedRoute><FarmerReviewsPage /></ProtectedRoute>} />
      <Route path="/u/:userId" element={<ProtectedRoute><FarmerProfilePage /></ProtectedRoute>} />
      <Route path="/farmer-dashboard" element={<ProtectedRoute requiredRole="farmer"><FarmerDashboard /></ProtectedRoute>} />
      <Route path="/create-post" element={<ProtectedRoute><CreatePostPage /></ProtectedRoute>} />
      <Route path="/edit-requirement/:id" element={<ProtectedRoute><CreatePostPage /></ProtectedRoute>} />
      <Route path="/my-requirements" element={<ProtectedRoute><MyRequirementsPage /></ProtectedRoute>} />
      {/* Legacy edit routes — kept so farmer dashboard edit links keep working */}
      <Route path="/create-produce" element={<ProtectedRoute requiredRole="farmer"><CreatePostPage /></ProtectedRoute>} />
      <Route path="/edit-produce/:id" element={<ProtectedRoute requiredRole="farmer"><CreatePostPage /></ProtectedRoute>} />
      <Route path="/kyc-apply" element={<ProtectedRoute><KYCApplicationPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminPage /></ProtectedRoute>} />
      <Route path="/org" element={<ProtectedRoute><OrgPage /></ProtectedRoute>} />
      <Route path="/org-register" element={<ProtectedRoute><OrgRegisterPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/saved" element={<ProtectedRoute><SavedPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/liked" element={<ProtectedRoute><LikedFarmersPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
